import {
    confirmSignUp as amplifyConfirmSignUp,
    signIn as amplifySignIn,
    signOut as amplifySignOut,
    signUp as amplifySignUp,
    confirmResetPassword,
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
    resendSignUpCode,
    resetPassword,
    updatePassword,
} from 'aws-amplify/auth';

const buildAuthenticatedUser = async () => {
    const { username } = await getCurrentUser();
    const { tokens } = await fetchAuthSession();

    return {
        username,
        signInUserSession: {
            idToken: {
                jwtToken: tokens?.idToken?.toString(),
                payload: tokens?.idToken?.payload,
            },
        },
    };
};

const normalizeError = (error: unknown): never => {
    const err = error as Record<string, unknown>;
    if (err && !err.code && err.name) {
        err.code = err.name;
    }
    throw error;
};

export const Auth = {
    currentAuthenticatedUser: async () => {
        try {
            return await buildAuthenticatedUser();
        } catch (error) {
            normalizeError(error);
        }
    },

    signIn: async (username: string, password: string) => {
        try {
            const { nextStep } = await amplifySignIn({ username, password });
            // v6 resolves an unconfirmed user instead of throwing; the login screens branch on the v5 error code.
            if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
                throw Object.assign(new Error('User is not confirmed.'), { name: 'UserNotConfirmedException' });
            }
            const user = await buildAuthenticatedUser();
            const attributes = await fetchUserAttributes();
            return { ...user, attributes };
        } catch (error) {
            normalizeError(error);
        }
    },

    currentUserAttributes: async () => {
        try {
            return await fetchUserAttributes();
        } catch (error) {
            return normalizeError(error);
        }
    },

    signOut: async () => {
        try {
            await amplifySignOut();
        } catch (error) {
            normalizeError(error);
        }
    },

    signUp: async ({
        username,
        password,
        attributes,
    }: {
        username: string;
        password: string;
        attributes: Record<string, string>;
    }) => {
        try {
            const result = await amplifySignUp({
                username,
                password,
                options: { userAttributes: attributes },
            });

            return {
                user: { username },
                userConfirmed: result.isSignUpComplete,
                userSub: result.userId,
            };
        } catch (error) {
            normalizeError(error);
        }
    },

    confirmSignUp: async (username: string, confirmationCode: string) => {
        try {
            const result = await amplifyConfirmSignUp({ username, confirmationCode });
            if (!result.isSignUpComplete) {
                throw new Error('Sign up confirmation incomplete');
            }
            return 'SUCCESS';
        } catch (error) {
            normalizeError(error);
        }
    },

    resendSignUp: async (username: string) => {
        try {
            return await resendSignUpCode({ username });
        } catch (error) {
            normalizeError(error);
        }
    },

    forgotPassword: async (username: string) => {
        try {
            const result = await resetPassword({ username });
            return {
                CodeDeliveryDetails: {
                    AttributeName: result.nextStep?.codeDeliveryDetails?.attributeName,
                    DeliveryMedium: result.nextStep?.codeDeliveryDetails?.deliveryMedium,
                    Destination: result.nextStep?.codeDeliveryDetails?.destination,
                },
            };
        } catch (error) {
            normalizeError(error);
        }
    },

    forgotPasswordSubmit: async (username: string, confirmationCode: string, newPassword: string) => {
        try {
            await confirmResetPassword({ username, confirmationCode, newPassword });
        } catch (error) {
            normalizeError(error);
        }
    },

    changePassword: async (_user: unknown, oldPassword: string, newPassword: string) => {
        try {
            await updatePassword({ oldPassword, newPassword });
            return 'SUCCESS';
        } catch (error) {
            normalizeError(error);
        }
    },
};
